'use memo';

import ActionButton from '@/components/Button/ActionButton';
import Icon from '@/components/Icon/Icon';
import { Text } from '@/components/Typography';
import type { SessionSummary } from '@/features/global-ai-chat/api/types';
import { getSessionDisplayTitle, groupSessionsByRecency } from '@/features/global-ai-chat/api/utils';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
type AiChatSessionListProps = {
  sessions: SessionSummary[];
  activeSessionId: string | null;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onSelect: (chatSessionId: string) => void;
  onFetchNextPage?: () => void;
};
const GROUP_LABEL_KEY = {
  today: 'historyGroupToday',
  past7Days: 'historyGroupPast7Days',
  past30Days: 'historyGroupPast30Days',
  older: 'historyGroupOlder'
} as const;
export function AiChatSessionList({
  sessions,
  activeSessionId,
  hasNextPage = false,
  isFetchingNextPage = false,
  onSelect,
  onFetchNextPage
}: AiChatSessionListProps) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.globalAiChat'
  });
  if (sessions.length === 0) {
    return <div className="flex h-full items-center justify-center px-4 py-8">
        <Text variant="pSmall" className="text-neutral-muted-on-fill">
          {t('historyEmpty')}
        </Text>
      </div>;
  }
  const groups = groupSessionsByRecency(sessions);
  return <div className="flex flex-col gap-3 px-2 py-2">
      {groups.map((group, index) => <section key={group.key} className={cn('flex flex-col gap-1', index === 0 ? '' : 'pt-2')}>
          <Text variant="labelExSmall" className="block px-3 pb-1 text-neutral-muted-on-fill">
            {t(GROUP_LABEL_KEY[group.key])}
          </Text>
          {group.sessions.map(session => {
        const isActive = session.chatSessionId === activeSessionId;
        const isClosed = session.status === 'closed';
        return <button key={session.chatSessionId} type="button" className={cn('flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left', 'hover:bg-primary-muted-fill', isActive ? 'bg-primary-muted-fill' : '')} onClick={() => onSelect(session.chatSessionId)}>
                <div className="min-w-0 flex-1">
                  <Text variant="labelSmall" className="block truncate text-neutral-on-fill">
                    {getSessionDisplayTitle(session, t('title'))}
                  </Text>
                  {isClosed ? <Text variant="labelExSmall" className="block text-neutral-muted-on-fill">
                      {t('closedStatusLabel')}
                    </Text> : null}
                </div>
                {isActive ? <span className="shrink-0 text-primary-on-neutral-fill" aria-hidden>
                    <Icon iconName="Check" size={14} inheritColor />
                  </span> : null}
              </button>;
      })}
        </section>)}

      {hasNextPage ? <div className="flex justify-center px-2 py-1">
          <ActionButton type="button" size="sm" variant="mutedGhost" disabled={isFetchingNextPage} onClick={() => onFetchNextPage?.()}>
            {isFetchingNextPage ? t('loading') : t('loadMoreSessionsAction')}
          </ActionButton>
        </div> : null}
    </div>;
}